import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  getFundDetail,
  patchFund,
  createFundNote,
  patchFundNote,
  deleteFundNote,
  createFundPosition,
  replaceFundPosition,
  patchFundPosition,
  deleteFundPosition,
  getFundChairOtherFunds,
  createFundChairRoleSlot,
  patchFundChairRoleSlot,
  deleteFundChairRoleSlot,
  createFundEmailContact,
  patchFundEmailContact,
  deleteFundEmailContact,
  createFundEmailRoleSlot,
  patchFundEmailRoleSlot,
  deleteFundEmailRoleSlot,
} from '../../endpoints/financeFunds';
import { getSPDChaptersList } from '../../endpoints/chaptersTs';
import type { SPDChapterListResponse } from '../../types/chapters';
import ChapterSelector from '../../Components/ChapterSelector';
import {
  FUND_CATEGORY_TYPE_ALLOCATION_ID,
  fundTypeRequiresCustomName,
} from '../../types/financeFunds';
import type {
  FundDetailResponse,
  PatchFundRequest,
  PatchFundPositionRequest,
  ReplaceFundPositionRequest,
  FundChairOtherFundDTO,
  CreateFundRoleSlotRequest,
  PatchFundRoleSlotRequest,
  CreateFundEmailContactRequest,
  PatchFundEmailContactRequest,
  FundAssignedTypeInput,
} from '../../types/financeFunds';
import PositionDisplay from '../../Components/PositionDisplay';
import FundRoleSlotDisplay from '../../Components/FundRoleSlotDisplay';
import AlertItem from '../../Components/AlertItem';
import FundAttachmentsTab from './FundAttachmentsTab';
import FundNamedAmountsSection from './FundNamedAmountsSection';
import FundTypeAllocationEditor, {
  AssignedTypeRow,
} from './FundTypeAllocationEditor';

interface PositionSaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const formatAllocationAmount = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FundDetail: React.FC = () => {
  const { id: fundId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [editingFund, setEditingFund] = useState(false);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [canDonate, setCanDonate] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subtypeId, setSubtypeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [detailTab, setDetailTab] = useState(0);
  const [spdChapterId, setSpdChapterId] = useState('');
  const [localChapterId, setLocalChapterId] = useState('');
  const [establishedBy, setEstablishedBy] = useState('');
  const [establishedDate, setEstablishedDate] = useState<Dayjs | null>(null);
  const [optOut, setOptOut] = useState(false);
  const [selectionCommittee, setSelectionCommittee] = useState('');
  const [transferLetter, setTransferLetter] = useState('');
  const [statementParagraph, setStatementParagraph] = useState('');
  const [activityLetter, setActivityLetter] = useState('');
  const [fundUse, setFundUse] = useState('');
  const [assignedTypes, setAssignedTypes] = useState<AssignedTypeRow[]>([]);

  const [newNote, setNewNote] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  const [fundError, setFundError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [positionSaving, setPositionSaving] = useState(false);
  const [positionError, setPositionError] = useState<string | null>(null);
  const [chairRoleSlotSaving, setChairRoleSlotSaving] = useState(false);
  const [chairRoleSlotError, setChairRoleSlotError] = useState<string | null>(
    null
  );
  const [emailRoleSlotSaving, setEmailRoleSlotSaving] = useState(false);
  const [emailRoleSlotError, setEmailRoleSlotError] = useState<string | null>(
    null
  );
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [chairConflict, setChairConflict] = useState<{
    funds: FundChairOtherFundDTO[];
    pending: {
      userId: string;
      roleId: string;
      startDate: string;
      endDate?: string | null;
      memberId?: string;
    };
  } | null>(null);

  const {
    data,
    isLoading,
    isError,
    error: loadError,
  } = useApiGet<FundDetailResponse, string>(
    ['fund-detail', fundId!],
    getFundDetail,
    { enabled: !!fundId, retry: 1 },
    fundId!
  );

  const syncFromData = useCallback((d: FundDetailResponse) => {
    setName(d.name || '');
    setShortName(d.short_name || '');
    setAccountId(d.account_id || '');
    setDescription(d.description || '');
    setCanDonate(d.can_donate === 1);
    setTypeId(d.type_id || '');
    setCategoryId(d.category_id || '');
    setSubtypeId(d.subtype_id || '');
    setStatusId(d.status_id || '');
    setSpdChapterId(d.spd_chapter_id || '');
    setLocalChapterId(d.local_chapter_id || '');
    setEstablishedBy(d.established_by || '');
    setEstablishedDate(d.established_date ? dayjs(d.established_date) : null);
    setOptOut(d.opt_out === 1);
    setSelectionCommittee(d.selection_committee || '');
    setTransferLetter(d.transfer_letter || '');
    setStatementParagraph(d.statement_paragraph || '');
    setActivityLetter(d.activity_letter || '');
    setFundUse(d.fund_use || '');
    if (d.uses_type_allocations) {
      if (d.assigned_types && d.assigned_types.length > 0) {
        setAssignedTypes(
          d.assigned_types.map((row) => ({
            type_id: row.type_id,
            subtype_id: row.subtype_id || '',
            percentage: String(row.percentage),
            other_transfer_name: row.other_transfer_name || '',
            principal_amount:
              row.principal_amount != null ? String(row.principal_amount) : '',
          }))
        );
      } else if (d.type_id) {
        setAssignedTypes([
          {
            type_id: d.type_id,
            subtype_id: d.subtype_id || '',
            percentage: '100',
            other_transfer_name: '',
            principal_amount: '',
          },
        ]);
      } else {
        setAssignedTypes([
          {
            type_id: '',
            subtype_id: '',
            percentage: '100',
            other_transfer_name: '',
            principal_amount: '',
          },
        ]);
      }
    } else {
      setAssignedTypes([]);
    }
  }, []);

  useEffect(() => {
    if (data && !editingFund) {
      syncFromData(data);
    }
  }, [data, editingFund, syncFromData]);

  const { data: spdChapters = [] } = useApiGet<SPDChapterListResponse[]>(
    ['fund-detail-spd-chapters'],
    getSPDChaptersList,
    { enabled: !!fundId, staleTime: 60_000 }
  );

  const spdLabel = (sp: SPDChapterListResponse) => {
    const abbr = sp.abbreviation?.trim();
    return abbr ? `${sp.name} (${abbr})` : sp.name;
  };

  const { mutate: patchFundMut } = useApiSend(
    (p: { id: string; body: PatchFundRequest }) => patchFund(p.id, p.body),
    () => {
      setEditingFund(false);
      setFundError(null);
    },
    (e: Error) => setFundError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: addNoteMut } = useApiSend(
    (p: { id: string; note: string }) => createFundNote(p.id, { note: p.note }),
    () => {
      setNewNote('');
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: patchNoteMut } = useApiSend(
    (p: { id: string; noteId: string; note: string }) =>
      patchFundNote(p.id, p.noteId, { note: p.note }),
    () => {
      setNoteDialogOpen(false);
      setEditNoteId(null);
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: deleteNoteMut } = useApiSend(
    (p: { id: string; noteId: string }) => deleteFundNote(p.id, p.noteId),
    () => {
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: addPositionMut } = useApiSend(
    (p: {
      id: string;
      body: {
        user_id: string;
        role_id: string;
        start_date: string;
        end_date?: string | null;
        apply_to_other_funds?: boolean;
      };
    }) => createFundPosition(p.id, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: replacePositionMut } = useApiSend(
    (p: { id: string; memberId: string; body: ReplaceFundPositionRequest }) =>
      replaceFundPosition(p.id, p.memberId, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: patchPositionMut } = useApiSend(
    (p: { id: string; memberId: string; body: PatchFundPositionRequest }) =>
      patchFundPosition(p.id, p.memberId, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deletePositionMut } = useApiSend(
    (p: { id: string; memberId: string }) =>
      deleteFundPosition(p.id, p.memberId),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: createChairRoleSlotMut } = useApiSend(
    (p: { id: string; body: CreateFundRoleSlotRequest }) =>
      createFundChairRoleSlot(p.id, p.body),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: patchChairRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string; body: PatchFundRoleSlotRequest }) =>
      patchFundChairRoleSlot(p.id, p.slotId, p.body),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteChairRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string }) =>
      deleteFundChairRoleSlot(p.id, p.slotId),
    () => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(null);
    },
    (e: Error) => {
      setChairRoleSlotSaving(false);
      setChairRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: addEmailContactMut } = useApiSend(
    (p: { id: string; body: CreateFundEmailContactRequest }) =>
      createFundEmailContact(p.id, p.body),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: patchEmailContactMut } = useApiSend(
    (p: { id: string; memberId: string; body: PatchFundEmailContactRequest }) =>
      patchFundEmailContact(p.id, p.memberId, p.body),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteEmailContactMut } = useApiSend(
    (p: { id: string; memberId: string }) =>
      deleteFundEmailContact(p.id, p.memberId),
    () => {
      setEmailSaving(false);
      setEmailError(null);
    },
    (e: Error) => {
      setEmailSaving(false);
      setEmailError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: createEmailRoleSlotMut } = useApiSend(
    (p: { id: string; body: CreateFundRoleSlotRequest }) =>
      createFundEmailRoleSlot(p.id, p.body),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutateAsync: patchEmailRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string; body: PatchFundRoleSlotRequest }) =>
      patchFundEmailRoleSlot(p.id, p.slotId, p.body),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deleteEmailRoleSlotMut } = useApiSend(
    (p: { id: string; slotId: string }) =>
      deleteFundEmailRoleSlot(p.id, p.slotId),
    () => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(null);
    },
    (e: Error) => {
      setEmailRoleSlotSaving(false);
      setEmailRoleSlotError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const canManage = data?.can_manage === true;
  const canManageAttachments = data?.can_manage_attachments === true;
  const showNamedAmounts = data?.uses_named_amounts === true;

  const typeOptions = useMemo(() => {
    const all = data?.fund_type_options ?? [];
    if (!categoryId) return [];
    return all.filter((t) => t.category_id === categoryId);
  }, [data?.fund_type_options, categoryId]);

  const subtypeOptions = useMemo(() => {
    const all = data?.fund_subtype_options ?? [];
    if (!typeId) return [];
    return all.filter((s) => s.type_id === typeId);
  }, [data?.fund_subtype_options, typeId]);

  const subtypeRequired = subtypeOptions.length > 0;

  const usesTypeAllocations = useMemo(
    () => categoryId === FUND_CATEGORY_TYPE_ALLOCATION_ID,
    [categoryId]
  );

  const activeEmailMembers = useMemo(() => {
    const today = dayjs().startOf('day');
    return (data?.email_members ?? []).filter(
      (m) => !m.end_date || !dayjs(m.end_date).startOf('day').isBefore(today)
    );
  }, [data?.email_members]);

  const emailMemberRoleName = data?.email_roles?.[0]?.name || 'Member';
  const emailMemberRoleId = data?.email_roles?.[0]?.id || '';

  const handleSaveFund = () => {
    if (!fundId) return;
    if (usesTypeAllocations) {
      if (assignedTypes.length < 1) {
        setFundError('At least one type allocation is required.');
        return;
      }
      const total = assignedTypes.reduce((sum, row) => {
        const n = parseFloat(row.percentage);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      if (Math.abs(total - 100) >= 0.01) {
        setFundError('Type allocation percentages must sum to 100.');
        return;
      }
      for (let i = 0; i < assignedTypes.length; i += 1) {
        const row = assignedTypes[i];
        if (!row.type_id) {
          setFundError(`Type is required for allocation row ${i + 1}.`);
          return;
        }
        const rowSubtypes = (data?.fund_subtype_options ?? []).filter(
          (s) => s.type_id === row.type_id
        );
        if (rowSubtypes.length > 0 && !row.subtype_id) {
          setFundError(`Subtype is required for allocation row ${i + 1}.`);
          return;
        }
        const pct = parseFloat(row.percentage);
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
          setFundError(
            `Percentage must be a number between 0 and 100 for allocation row ${i + 1}.`
          );
          return;
        }
        if (
          fundTypeRequiresCustomName(row.type_id) &&
          !row.other_transfer_name.trim()
        ) {
          setFundError(`Custom name is required for allocation row ${i + 1}.`);
          return;
        }
        if (row.subtype_id && row.principal_amount.trim()) {
          const amount = parseFloat(row.principal_amount);
          if (!Number.isFinite(amount) || amount <= 0) {
            setFundError(
              `Amount must be greater than zero for allocation row ${i + 1}.`
            );
            return;
          }
        }
      }
    } else if (subtypeRequired && !subtypeId) {
      setFundError('Subtype is required for the selected type.');
      return;
    }
    const assignedPayload: FundAssignedTypeInput[] | undefined =
      usesTypeAllocations
        ? assignedTypes.map((row) => ({
            type_id: row.type_id,
            subtype_id: row.subtype_id || null,
            percentage: parseFloat(row.percentage),
            other_transfer_name: fundTypeRequiresCustomName(row.type_id)
              ? row.other_transfer_name.trim()
              : undefined,
            principal_amount:
              row.subtype_id && row.principal_amount.trim()
                ? parseFloat(row.principal_amount)
                : undefined,
          }))
        : undefined;
    const body: PatchFundRequest = {
      name,
      short_name: shortName,
      account_id: accountId,
      description,
      can_donate: canDonate ? 1 : 0,
      category_id: categoryId || null,
      type_id: usesTypeAllocations ? null : typeId || null,
      subtype_id: usesTypeAllocations
        ? null
        : subtypeRequired
          ? subtypeId || null
          : null,
      assigned_types: usesTypeAllocations
        ? (assignedPayload ?? null)
        : undefined,
      status_id: statusId || null,
      // Empty string clears; JSON null is skipped by the Go *string binding.
      spd_chapter_id: spdChapterId || '',
      local_chapter_id: localChapterId || '',
      established_by: establishedBy,
      // Empty string clears the date; JSON null is skipped by the Go *string binding.
      established_date: establishedDate
        ? establishedDate.format('YYYY-MM-DD')
        : '',
      opt_out: optOut ? 1 : 0,
      selection_committee: selectionCommittee,
      transfer_letter: transferLetter,
      statement_paragraph: statementParagraph,
      activity_letter: activityLetter,
      fund_use: fundUse,
    };
    patchFundMut({ id: fundId, body });
  };

  const fundFieldDisplay = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '—';
  };

  const handleAddNote = () => {
    if (!fundId || !newNote.trim()) return;
    addNoteMut({ id: fundId, note: newNote.trim() });
  };

  const openEditNote = (noteId: string, text: string) => {
    setEditNoteId(noteId);
    setEditNoteText(text);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (!fundId || !editNoteId) return;
    patchNoteMut({ id: fundId, noteId: editNoteId, note: editNoteText.trim() });
  };

  const handleDeleteNote = (noteId: string) => {
    if (!fundId) return;
    if (!window.confirm('Delete this note?')) return;
    deleteNoteMut({ id: fundId, noteId });
  };

  const runChairAppointment = (
    pending: {
      userId: string;
      roleId: string;
      startDate: string;
      endDate?: string | null;
      memberId?: string;
    },
    applyToOtherFunds: boolean
  ) => {
    if (!fundId) return;
    setPositionSaving(true);
    setPositionError(null);
    if (pending.memberId) {
      replacePositionMut({
        id: fundId,
        memberId: pending.memberId,
        body: {
          user_id: pending.userId,
          start_date: pending.startDate,
          end_date: pending.endDate || undefined,
          apply_to_other_funds: applyToOtherFunds,
        },
      });
    } else {
      addPositionMut({
        id: fundId,
        body: {
          user_id: pending.userId,
          role_id: pending.roleId,
          start_date: pending.startDate,
          end_date: pending.endDate || undefined,
          apply_to_other_funds: applyToOtherFunds,
        },
      });
    }
  };

  const onSavePosition = (save: PositionSaveData) => {
    if (!fundId) return;
    setPositionSaving(true);
    setPositionError(null);
    if (save.action === 'removeRecord') {
      deletePositionMut({ id: fundId, memberId: save.id });
      return;
    }
    if (save.action === 'editDates') {
      const body: PatchFundPositionRequest = {
        start_date: save.startDate || undefined,
        end_date:
          save.endDate === null || save.endDate === undefined
            ? ''
            : save.endDate,
      };
      if (save.positionId) {
        body.role_id = save.positionId;
      }
      patchPositionMut({ id: fundId, memberId: save.id, body });
      return;
    }
    if (save.action === 'newAppointment') {
      if (!save.userId || !save.startDate || !save.positionId) {
        setPositionSaving(false);
        setPositionError('Member, role, and start date are required.');
        return;
      }
      const pending = {
        userId: save.userId,
        roleId: save.positionId,
        startDate: save.startDate,
        endDate: save.endDate,
        memberId: save.id || undefined,
      };
      getFundChairOtherFunds(fundId, save.userId, save.positionId)
        .then(({ other_funds }) => {
          if (other_funds.length === 0) {
            runChairAppointment(pending, false);
          } else {
            setPositionSaving(false);
            setChairConflict({ funds: other_funds, pending });
          }
        })
        .catch((e: unknown) => {
          setPositionSaving(false);
          setPositionError(getApiErrorMessage(e));
        });
    }
  };

  const onSaveEmailContact = (save: PositionSaveData) => {
    if (!fundId) return;
    setEmailSaving(true);
    setEmailError(null);
    if (save.action === 'removeRecord') {
      deleteEmailContactMut({ id: fundId, memberId: save.id });
      return;
    }
    if (save.action === 'editDates') {
      const body: PatchFundEmailContactRequest = {
        start_date: save.startDate || undefined,
        end_date:
          save.endDate === null || save.endDate === undefined
            ? ''
            : save.endDate,
      };
      if (save.userId) {
        body.user_id = save.userId;
      }
      patchEmailContactMut({ id: fundId, memberId: save.id, body });
      return;
    }
    if (save.action === 'newAppointment') {
      if (!save.userId || !save.startDate) {
        setEmailSaving(false);
        setEmailError('Member and start date are required.');
        return;
      }
      addEmailContactMut({
        id: fundId,
        body: {
          user_id: save.userId,
          start_date: save.startDate,
          end_date: save.endDate || undefined,
        },
      });
    }
  };

  if (!fundId) {
    return null;
  }

  if (isLoading) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Typography>Loading…</Typography>
      </Grid>
    );
  }

  if (isError || !data) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Grid item xs={12}>
          <AlertItem
            severity="error"
            description={getApiErrorMessage(loadError)}
            onClose={() => {}}
            keepOpen
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/finance/funds')}
          >
            Back to funds
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2} spacing={2} sx={{ pb: 4 }}>
      <Grid item xs={12}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/finance/funds')}
          sx={{ mb: 1 }}
        >
          Back to funds
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={data.name || 'Fund'}
            subheader={data.account_id || data.id}
          />
          <CardContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {fundFieldDisplay(editingFund ? description : data.description)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Tabs
          value={detailTab}
          onChange={(_, v: number) => setDetailTab(v)}
          sx={{ mb: 1 }}
        >
          <Tab label="Information" />
          <Tab label="Positions" />
          <Tab label="Notes" />
          <Tab label="Attachments" />
        </Tabs>
      </Grid>

      {detailTab === 0 && (
        <>
          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader
                title="Fund"
                subheader={data.account_id || data.id}
                action={
                  canManage ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {editingFund ? (
                        <>
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setEditingFund(false);
                              syncFromData(data);
                              setFundError(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<Save />}
                            onClick={handleSaveFund}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => setEditingFund(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  ) : undefined
                }
              />
              <CardContent>
                {fundError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={fundError}
                      onClose={() => setFundError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Short name"
                          value={shortName}
                          onChange={(e) => setShortName(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account"
                          value={accountId}
                          onChange={(e) => setAccountId(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={canDonate}
                              onChange={(e) => setCanDonate(e.target.checked)}
                            />
                          }
                          label="Can donate"
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Name:</b> {data.name || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Short name:</b> {data.short_name || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Account:</b> {data.account_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <b>Can donate:</b>{' '}
                          {data.can_donate === 1 ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Chapter & classification" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            label="Status"
                            value={statusId}
                            onChange={(e) =>
                              setStatusId(String(e.target.value))
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {(data.fund_status_options ?? []).map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>SPD chapter</InputLabel>
                          <Select
                            label="SPD chapter"
                            value={spdChapterId}
                            onChange={(e) => {
                              setSpdChapterId(String(e.target.value));
                              setLocalChapterId('');
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {spdChapters.map((sp) => (
                              <MenuItem key={sp.id} value={sp.id}>
                                {spdLabel(sp)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ChapterSelector
                          chapter={localChapterId}
                          spd={spdChapterId || null}
                          all={!spdChapterId}
                          keyId={`fund-detail-${fundId}-lc`}
                          label="Local chapter"
                          handleChapterSelect={(chapterId) =>
                            setLocalChapterId(chapterId || '')
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Category</InputLabel>
                          <Select
                            label="Category"
                            value={categoryId}
                            onChange={(e) => {
                              const next = String(e.target.value);
                              setCategoryId(next);
                              const types = (
                                data.fund_type_options ?? []
                              ).filter((t) => t.category_id === next);
                              if (next === FUND_CATEGORY_TYPE_ALLOCATION_ID) {
                                setTypeId('');
                                setSubtypeId('');
                                if (assignedTypes.length === 0) {
                                  setAssignedTypes([
                                    {
                                      type_id: '',
                                      subtype_id: '',
                                      percentage: '100',
                                      other_transfer_name: '',
                                      principal_amount: '',
                                    },
                                  ]);
                                }
                              } else if (!types.some((t) => t.id === typeId)) {
                                setTypeId('');
                                setSubtypeId('');
                                setAssignedTypes([]);
                              } else {
                                const subtypes = (
                                  data.fund_subtype_options ?? []
                                ).filter((s) => s.type_id === typeId);
                                if (!subtypes.some((s) => s.id === subtypeId)) {
                                  setSubtypeId('');
                                }
                                setAssignedTypes([]);
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {data.fund_category_options.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {usesTypeAllocations ? (
                        <FundTypeAllocationEditor
                          rows={assignedTypes}
                          categoryId={categoryId}
                          typeOptions={data.fund_type_options ?? []}
                          subtypeOptions={data.fund_subtype_options ?? []}
                          onChange={setAssignedTypes}
                        />
                      ) : (
                        <>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                label="Type"
                                value={typeId}
                                onChange={(e) => {
                                  const next = String(e.target.value);
                                  setTypeId(next);
                                  const subtypes = (
                                    data.fund_subtype_options ?? []
                                  ).filter((s) => s.type_id === next);
                                  if (
                                    !subtypes.some((s) => s.id === subtypeId)
                                  ) {
                                    setSubtypeId('');
                                  }
                                }}
                                disabled={!categoryId}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {typeOptions.map((t) => (
                                  <MenuItem key={t.id} value={t.id}>
                                    {t.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {subtypeOptions.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small" required>
                                <InputLabel>Subtype</InputLabel>
                                <Select
                                  label="Subtype"
                                  value={subtypeId}
                                  onChange={(e) =>
                                    setSubtypeId(String(e.target.value))
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select subtype</em>
                                  </MenuItem>
                                  {subtypeOptions.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </>
                      )}
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Established by"
                          value={establishedBy}
                          onChange={(e) => setEstablishedBy(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Established date"
                          value={establishedDate}
                          onChange={(v) => setEstablishedDate(v)}
                          slotProps={{
                            textField: { fullWidth: true, size: 'small' },
                          }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Status:</b>{' '}
                          {data.status_name || data.status_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>SPD chapter:</b>{' '}
                          {data.spd_chapter_name || data.spd_chapter_id || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Local chapter:</b>{' '}
                          {data.local_chapter_name ||
                            data.local_chapter_id ||
                            '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Category:</b> {data.category_name || '—'}
                        </Typography>
                      </Grid>
                      {data.uses_type_allocations ? (
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <b>Type allocations:</b>
                          </Typography>
                          {(data.assigned_types ?? []).length > 0 ? (
                            (data.assigned_types ?? []).map((row, i) => (
                              <Typography
                                key={
                                  row.id ||
                                  `${row.type_id}-${row.subtype_id ?? ''}-${row.percentage}-${i}`
                                }
                                variant="body2"
                              >
                                {row.type_name || row.type_id}
                                {row.other_transfer_name
                                  ? ` — ${row.other_transfer_name}`
                                  : ''}
                                {row.subtype_name
                                  ? ` (${row.subtype_name})`
                                  : ''}
                                {row.principal_amount != null &&
                                row.principal_amount > 0
                                  ? ` — $${formatAllocationAmount(row.principal_amount)}`
                                  : ''}
                                {' — '}
                                {row.percentage}%
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2">
                              <b>Type:</b> {data.type_name || '—'}
                            </Typography>
                          </Grid>
                          {data.subtype_name && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2">
                                <b>Subtype:</b> {data.subtype_name}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Established by:</b> {data.established_by || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <b>Established date:</b>{' '}
                          {data.established_date || '—'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {showNamedAmounts && (
            <Grid item xs={12}>
              <FundNamedAmountsSection
                fundId={fundId}
                categoryId={data.category_id || ''}
                namedAmounts={data.named_amounts ?? []}
                typeOptions={data.fund_type_options ?? []}
                canManage={canManage}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund letters" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label="Transfer letter"
                          value={transferLetter}
                          onChange={(e) => setTransferLetter(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Statement paragraph"
                          value={statementParagraph}
                          onChange={(e) =>
                            setStatementParagraph(e.target.value)
                          }
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Activity letter"
                          value={activityLetter}
                          onChange={(e) => setActivityLetter(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Transfer letter:</b>{' '}
                          {fundFieldDisplay(data.transfer_letter)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Statement paragraph:</b>{' '}
                          {fundFieldDisplay(data.statement_paragraph)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Activity letter:</b>{' '}
                          {fundFieldDisplay(data.activity_letter)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund options" />
              <CardContent>
                <Grid container spacing={2}>
                  {editingFund ? (
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={optOut}
                              onChange={(e) => setOptOut(e.target.checked)}
                            />
                          }
                          label="Opt out"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Selection committee"
                          value={selectionCommittee}
                          onChange={(e) =>
                            setSelectionCommittee(e.target.value)
                          }
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: 255 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Fund use"
                          value={fundUse}
                          onChange={(e) => setFundUse(e.target.value)}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <b>Opt out:</b> {data.opt_out === 1 ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Selection committee:</b>{' '}
                          {fundFieldDisplay(data.selection_committee)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          <b>Fund use:</b> {fundFieldDisplay(data.fund_use)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {detailTab === 1 && (
        <>
          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Chair / Co-Chair" />
              <CardContent>
                {positionError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={positionError}
                      onClose={() => setPositionError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                {data.chair_roles.map((role) => {
                  const active = data.positions.find(
                    (p) => p.position_id === role.id
                  );
                  return (
                    <PositionDisplay
                      key={role.id}
                      id={active?.id ?? ''}
                      position={role.name}
                      positionId={role.id}
                      committeeId={undefined}
                      name={active?.name}
                      startDate={active?.start_date || null}
                      endDate={active?.end_date ?? null}
                      canEdit={canManage}
                      isSaving={positionSaving}
                      errorSavingText={positionError || undefined}
                      onSave={onSavePosition}
                      userId={active?.user_id}
                      pageFrom="finance-fund-chairs"
                      fundId={fundId}
                      showRoleHistory={canManage}
                      canMessage={false}
                      chairRoleOptions={data.chair_roles}
                    />
                  );
                })}
                {data.chair_roles.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No chair roles configured.
                  </Typography>
                )}
                {chairRoleSlotError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={chairRoleSlotError}
                      onClose={() => setChairRoleSlotError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <FundRoleSlotDisplay
                  title="Role-based assignments"
                  slots={data.chair_role_slots ?? []}
                  groupRoleOptions={data.chair_roles}
                  refRoleOptions={data.ref_role_options ?? []}
                  spdChapters={spdChapters}
                  canEdit={canManage}
                  isSaving={chairRoleSlotSaving}
                  onCreate={async (body) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    await createChairRoleSlotMut({ id: fundId, body });
                  }}
                  onPatch={async (slotId, body) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    await patchChairRoleSlotMut({ id: fundId, slotId, body });
                  }}
                  onDelete={(slotId) => {
                    if (!fundId) return;
                    setChairRoleSlotSaving(true);
                    setChairRoleSlotError(null);
                    deleteChairRoleSlotMut({ id: fundId, slotId });
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Fund Email Contacts" />
              <CardContent>
                {emailError && (
                  <Box sx={{ mb: 2 }}>
                    <AlertItem
                      severity="error"
                      description={emailError}
                      onClose={() => setEmailError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <Typography variant="subtitle2" gutterBottom>
                  Members
                </Typography>
                {activeEmailMembers.map((member) => (
                  <PositionDisplay
                    key={member.id}
                    id={member.id}
                    position={member.position || emailMemberRoleName}
                    positionId={member.position_id || emailMemberRoleId}
                    committeeId={undefined}
                    name={member.name}
                    startDate={member.start_date || null}
                    endDate={member.end_date ?? null}
                    canEdit={canManage}
                    isSaving={emailSaving}
                    errorSavingText={emailError || undefined}
                    onSave={onSaveEmailContact}
                    userId={member.user_id}
                    pageFrom="finance-fund-email"
                    fundId={fundId}
                    showRoleHistory={false}
                    canMessage={false}
                  />
                ))}
                {activeEmailMembers.length === 0 && !canManage && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No active email contacts.
                  </Typography>
                )}
                {canManage && (
                  <PositionDisplay
                    id=""
                    position={emailMemberRoleName}
                    positionId={emailMemberRoleId}
                    committeeId={undefined}
                    canEdit={canManage}
                    isSaving={emailSaving}
                    errorSavingText={emailError || undefined}
                    onSave={onSaveEmailContact}
                    pageFrom="finance-fund-email"
                    fundId={fundId}
                    showRoleHistory={false}
                    canMessage={false}
                  />
                )}
                {emailRoleSlotError && (
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <AlertItem
                      severity="error"
                      description={emailRoleSlotError}
                      onClose={() => setEmailRoleSlotError(null)}
                      keepOpen
                    />
                  </Box>
                )}
                <FundRoleSlotDisplay
                  title="Role-based email assignments"
                  slots={data.email_role_slots ?? []}
                  groupRoleOptions={data.email_roles ?? []}
                  refRoleOptions={data.ref_role_options ?? []}
                  spdChapters={spdChapters}
                  canEdit={canManage}
                  isSaving={emailRoleSlotSaving}
                  fixedGroupRoleId={emailMemberRoleId || undefined}
                  onCreate={async (body) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    await createEmailRoleSlotMut({ id: fundId, body });
                  }}
                  onPatch={async (slotId, body) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    await patchEmailRoleSlotMut({ id: fundId, slotId, body });
                  }}
                  onDelete={(slotId) => {
                    if (!fundId) return;
                    setEmailRoleSlotSaving(true);
                    setEmailRoleSlotError(null);
                    deleteEmailRoleSlotMut({ id: fundId, slotId });
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {detailTab === 2 && (
        <Grid item xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Fund notes" />
            <CardContent>
              {noteError && (
                <Box sx={{ mb: 2 }}>
                  <AlertItem
                    severity="error"
                    description={noteError}
                    onClose={() => setNoteError(null)}
                    keepOpen
                  />
                </Box>
              )}
              {data.notes.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No notes yet.
                </Typography>
              )}
              {data.notes.map((n) => (
                <Box key={n.id} sx={{ mb: 2 }}>
                  <Typography variant="body2">{n.note}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.created_by_name || n.created_by_id}
                    {n.created_at
                      ? ` · ${dayjs(n.created_at).format('MM/DD/YYYY h:mm A')}`
                      : ''}
                  </Typography>
                  {canManage && (
                    <Box sx={{ mt: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Edit note"
                        onClick={() => openEditNote(n.id, n.note)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete note"
                        onClick={() => handleDeleteNote(n.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
              {canManage && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="New note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                  />
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                  >
                    Add note
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {detailTab === 3 && (
        <Grid item xs={12}>
          <FundAttachmentsTab
            fundId={fundId}
            canManage={canManageAttachments}
          />
        </Grid>
      )}

      <Dialog
        open={chairConflict !== null}
        onClose={() => setChairConflict(null)}
      >
        <DialogTitle>Same chair role on other funds</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This member holds the same chair role on the following funds. Apply
            this appointment to this fund only, or to all listed funds using
            that role?
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {chairConflict?.funds.map((f) => (
              <Typography component="li" variant="body2" key={f.fund_id}>
                {f.fund_name || f.fund_id}
                {f.role_name ? ` (${f.role_name})` : ''}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChairConflict(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (chairConflict) {
                runChairAppointment(chairConflict.pending, false);
                setChairConflict(null);
              }
            }}
          >
            This fund only
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (chairConflict) {
                runChairAppointment(chairConflict.pending, true);
                setChairConflict(null);
              }
            }}
          >
            All funds
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            minRows={3}
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FundDetail;
